import React, { useEffect, useState } from 'react';
import { Route, Switch } from 'react-router';
import ExperimentDashboard from 'components/main/ExperimentDashboard';
import AddProtein from 'components/main/ProteinFormulation/AddProtein';
import Cookies from 'js-cookie';
import Layout from './components/main/Layout';
import Login from './components/main/Login/Login';
import Logout from 'components/main/Login/Logout';
import './App.scss';
import TreeControl from './components/main/TreeControl';
import ProjectDashboard from 'components/main/ProjectDashboard';
import { isNullOrUndefined } from 'utils/Global';
import LicenseViolate from 'components/main/Licensing/LicenseViolation';
import RegisterComputer from 'components/main/Licensing/RegisterComputer';
import SessionTermination from 'components/main/Licensing/SessionTermination';
import UpdatedDashboard from 'components/UpdatedDashboard/Dashboard';
import RMMessage from 'components/main/RMMessage';
import HybridSelection from 'components/main/Licensing/HybridSelection';
import SearchComponent from 'components/UpdatedDashboard/SearchComponent';
import SeedDashboard from 'components/main/SeedDashboard';
import AccessControlError from 'components/main/Error/AccessControlError';
import VersionMismatchError from 'components/main/Error/VersionMismatchError';
import { SetupTabs } from 'components/main/Setup/GeneralSetup/SetupTabs';
import { crystal, synchrotron } from './pages';
import SynchrotronTrip from 'components/synchrotron';
import { SettingsTabs } from 'components/main/Settings/SettingsTabs';
import { ModalContext } from 'components/modal/modalContext';
import {
  BackdropLoadingContainer,
  Modal,
  ModalProps,
  defaultAlertModalState,
} from 'components/modal';
import { Fragment } from 'components/main/Fragment/Fragment';
import SearchAndFilter from 'pages/searchAndFilter';
import FeatureSelection from 'components/main/Licensing/FeatureSelection';
import ForgotPassword from 'components/main/userGroup/user/forgotPassword';
import InputOTP from 'components/main/userGroup/user/inputOTP';
import useRMLite from 'CustomHooks/useRMLite';
import { RightClickProvider } from 'components/rightClick/rightClickProvider';
import RightClickWrapper from 'components/rightClick/rightClickWrapper';
import GuardedRoute from 'components/GuardedRoute';
import { CacheHelper } from 'utils/CacheHelper';

const rmliteRestrictedRoutes = [
  { path: '/seed/:id', component: SeedDashboard },
  { path: '/protein/:nodeId', component: AddProtein },
  { path: '/screen/:id', component: ExperimentDashboard },
  { path: '/synchrotrontrip/:id', component: SynchrotronTrip },
  { path: '/setup/:name', component: SetupTabs },
  { path: '/synchrotron', component: synchrotron },
  { path: '/crystal', component: crystal },
  { path: '/settings/:tab?', component: SettingsTabs },
  { path: '/snf/:id', component: SearchAndFilter },
  { path: '/fragment/:id', component: Fragment },
];

const rmLiteRestrictionMessage =
  'The feature you are trying to access is not available in Rock Maker Lite.';
import WellDropImageStore from 'components/well/wellDropImageStore';
import store from 'redux/configureStore';
import withErrorBoundary from 'components/main/Error/withErrorBoundary';
import { useFetchLicenseValidatorConfig } from 'business/licenseValidator/hooks';
import SeatLicenseViolation from 'components/main/Licensing/SeatLicenseViolation';

const App = () => {
  const [modalDetails, setModalDetails] = useState<ModalProps>(defaultAlertModalState);
  const isRMLite = useRMLite();
  useFetchLicenseValidatorConfig();

  const onUnload = async () => {
    // the method that will be used for both add and remove event
    // e.preventDefault();
    const cookiesTabs =
      Cookies.get('openedTabs') === undefined ? 0 : Cookies.get('openedTabs');
    const openedTabs = parseInt(cookiesTabs) - 1;
    if (openedTabs > 0) {
      Cookies.set('openedTabs', openedTabs);
    } else if (openedTabs === 0) {
      localStorage.removeItem('openedNodesIds');
      const token = await CacheHelper.get('token');
      CacheHelper.put('token', token);
      Cookies.set('openedTabs', openedTabs);
    }
  };

  const onLoad = async (e) => {
    // the method that will be used for both add and remove event
    e.preventDefault();
    const cookiesTabs =
      Cookies.get('openedTabs') === undefined ? 0 : Cookies.get('openedTabs');
    const openedTabs = parseInt(cookiesTabs) + 1;
    if (openedTabs === 1) {
      const token = await CacheHelper.get('token');
      if (!isNullOrUndefined(token)) {
        await CacheHelper.put('token', token);
      }
    }
    Cookies.set('openedTabs', openedTabs);
  };

  // This function can be used to initialize any global state or perform setup tasks
  const handleInitialization = () => {
    WellDropImageStore.injectDispatch(store.dispatch);
  };

  useEffect(() => {
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('load', onLoad);
    handleInitialization();

    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('load', onLoad);
    };
  }, []);

  return (
    <ModalContext.Provider value={{ modalDetails, setModalDetails }}>
      <BackdropLoadingContainer>
        <Layout>
          <RightClickProvider>
            <RightClickWrapper>
              <Switch>
                <Route exact path="/" component={UpdatedDashboard} />
                <Route exact path="/search" component={SearchComponent} />
                <Route path="/project/:treeNodeId?" component={ProjectDashboard} />
                <Route path="/login" component={Login} />
                <Route path="/logout" component={Logout} />
                <Route path="/inputOTP" component={InputOTP} />
                <Route path="/forgotPassword" component={ForgotPassword} />
                <Route path="/exp/:id" component={ExperimentDashboard} />
                <Route path="/explore" component={TreeControl} />
                <Route path="/licenseviolate" component={LicenseViolate} />
                <Route path="/rmmessage" component={RMMessage} />
                <Route path="/registercomputer" component={RegisterComputer} />
                <Route path="/sessiontermination" component={SessionTermination} />
                <Route path="/hybridselection" component={HybridSelection} />
                <Route path="/featureselection" component={FeatureSelection} />
                <Route path="/accesscontrolerror" component={AccessControlError} />
                <Route path="/versionmismatch" component={VersionMismatchError} />
                <Route path="/seatlicenseviolate" component={SeatLicenseViolation} />
                {rmliteRestrictedRoutes.map(({ path, component }) => (
                  <GuardedRoute
                    key={path}
                    path={path}
                    component={component}
                    isAllowed={!isRMLite}
                    message={rmLiteRestrictionMessage}
                  />
                ))}
              </Switch>
            </RightClickWrapper>
          </RightClickProvider>
        </Layout>
      </BackdropLoadingContainer>
      <Modal {...modalDetails} />
    </ModalContext.Provider>
  );
};

export default withErrorBoundary(App);
